/*
 * Client for the API, always on the same origin (/api/...). The session travels in
 * the HttpOnly cookie (never readable from JavaScript); requests that change data
 * carry the CSRF token given by GET /auth/session.
 */

export interface ApiIssue {
  path: string;
  message: string;
}

/** An error answered by the API: { error: { code, message, issues?, requestId? } }. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: ApiIssue[];
  readonly requestId: string | null;

  constructor(
    status: number,
    body: { code?: string; message?: string; issues?: ApiIssue[]; requestId?: string },
  ) {
    super(body.message ?? 'Ocurrió un error inesperado. Intenta de nuevo.');
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code ?? 'unknown_error';
    this.issues = body.issues ?? [];
    this.requestId = body.requestId ?? null;
  }
}

/** The server could not be reached (offline, DNS, timeout...). */
export class NetworkError extends Error {
  constructor() {
    super('No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.');
    this.name = 'NetworkError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface Config {
  csrfToken: string | null;
  /** Called once when the API rejects the CSRF token: returns a fresh one. */
  refreshCsrf: (() => Promise<string | null>) | null;
  fetch: typeof fetch;
}

const config: Config = { csrfToken: null, refreshCsrf: null, fetch: (...args) => fetch(...args) };

export function setCsrfToken(token: string | null): void {
  config.csrfToken = token;
}

export function configureApi(options: Partial<Config>): void {
  Object.assign(config, options);
}

export async function api<T>(
  path: string,
  options: { method?: Method; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const response = await send(path, options);
  if (
    response.status === 403 &&
    config.refreshCsrf &&
    (await peekCode(response)) === 'csrf_invalid'
  ) {
    config.csrfToken = await config.refreshCsrf();
    return parse<T>(await send(path, options));
  }
  return parse<T>(response);
}

async function send(
  path: string,
  options: { method?: Method; body?: unknown; signal?: AbortSignal },
) {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  const init: RequestInit = { method, headers, credentials: 'same-origin' };
  if (options.signal) init.signal = options.signal;
  if (method !== 'GET') {
    if (config.csrfToken) headers['x-csrf-token'] = config.csrfToken;
    if (options.body instanceof FormData) {
      // File upload: the browser sets multipart/form-data with its boundary.
      init.body = options.body;
    } else {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(options.body ?? {});
    }
  }
  try {
    return await config.fetch(`/api${path}`, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }
}

async function peekCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as { error?: { code?: string } };
    return body.error?.code ?? null;
  } catch {
    return null;
  }
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 202) return undefined as T;
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const error =
      (body as { error?: ConstructorParameters<typeof ApiError>[1] } | null)?.error ?? {};
    throw new ApiError(response.status, error);
  }
  return body as T;
}

/** A user-facing message for any error (always in Spanish, saying what to do). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) {
    const base = error.message;
    return error instanceof ApiError && error.requestId
      ? `${base} (código: ${error.requestId})`
      : base;
  }
  return 'Ocurrió un error inesperado. Recarga la página e intenta de nuevo.';
}
